import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { Box, Chip, Stack, Typography } from '@mui/material';

import palette from 'src/theme/palette';

import { ProductThumb } from './ProductThumb';
import type { RetentionInsight } from './types';

interface InsightsPanelProps {
  insights: RetentionInsight[];
}

const severityColorMap: Record<RetentionInsight['severity'], string> = {
  info: '#6366f1',
  opportunity: '#16a34a',
  warning: '#f59e0b',
};

/** Scala unica per tutte le card (allineata al toolkit Retention). */
const insightTitleSx = {
  fontWeight: 700,
  color: palette.text.primary,
  fontSize: '0.875rem',
  lineHeight: 1.35,
} as const;

const insightMetricSx = {
  display: 'block',
  mt: 0.35,
  fontSize: '0.75rem',
  lineHeight: 1.35,
  fontWeight: 700,
} as const;

const insightBodySx = {
  color: palette.text.secondary,
  mt: 1,
  fontSize: '0.8125rem',
  lineHeight: 1.45,
  fontWeight: 400,
} as const;

const insightActionSx = {
  color: palette.text.primary,
  mt: 1,
  fontSize: '0.8125rem',
  lineHeight: 1.45,
  fontWeight: 600,
} as const;

const insightIndexSx = {
  minWidth: 18,
  fontWeight: 800,
  fontSize: '0.875rem',
  lineHeight: 1,
} as const;

export function InsightsPanel({ insights }: InsightsPanelProps) {
  return (
    <Stack spacing={1.25}>
      {insights.map((insight, index) => (
        <Box
          key={`${insight.type}-${index}`}
          sx={{
            p: 1.5,
            borderRadius: 2.5,
            border: '1px solid',
            borderColor: `${severityColorMap[insight.severity]}26`,
            background:
              'linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(248,250,252,0.96) 100%)',
          }}
        >
          <Box
            className="retention-insight-card-header"
            display="flex"
            alignItems="flex-start"
            justifyContent="space-between"
            gap={1.5}
          >
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              sx={{ minWidth: 0, flex: 1 }}
            >
              <Typography
                sx={{
                  ...insightIndexSx,
                  color: severityColorMap[insight.severity],
                }}
              >
                {index + 1}
              </Typography>

              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 99,
                  bgcolor: `${severityColorMap[insight.severity]}18`,
                  color: severityColorMap[insight.severity],
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {insight.severity === 'warning' ? (
                  <WarningAmberRoundedIcon sx={{ fontSize: 16 }} />
                ) : (
                  <AutoAwesomeRoundedIcon sx={{ fontSize: 16 }} />
                )}
              </Box>

              {(insight.primaryProductImageUrl ||
                insight.secondaryProductImageUrl) && (
                <Box display="flex" alignItems="center" gap={0.75}>
                  {insight.primaryProductImageUrl && (
                    <ProductThumb
                      imageUrl={insight.primaryProductImageUrl}
                      title={insight.title}
                      size={32}
                      radius={9}
                    />
                  )}
                  {insight.secondaryProductImageUrl && (
                    <ProductThumb
                      imageUrl={insight.secondaryProductImageUrl}
                      title={insight.title}
                      size={32}
                      radius={9}
                    />
                  )}
                </Box>
              )}

              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  className="retention-pdf-unclamp"
                  sx={insightTitleSx}
                >
                  {insight.title}
                </Typography>
                {insight.metricLabel && (
                  <Typography
                    sx={{
                      ...insightMetricSx,
                      color: severityColorMap[insight.severity],
                    }}
                  >
                    {insight.metricLabel}
                  </Typography>
                )}
              </Box>
            </Box>
            <Chip
              label={insight.severity}
              size="small"
              sx={{
                flexShrink: 0,
                textTransform: 'capitalize',
                bgcolor: `${severityColorMap[insight.severity]}16`,
                color: severityColorMap[insight.severity],
                fontWeight: 700,
                fontSize: '0.6875rem',
                height: 22,
              }}
            />
          </Box>
          <Typography sx={insightBodySx}>{insight.description}</Typography>
          <Typography sx={insightActionSx}>
            {insight.recommendedAction}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}

export default InsightsPanel;
